'use client';
import DynamicTable from '@/components/molecules/DynamicTable/DynamicTable';
import { buildListenOptions, resolveChannel } from '@/packages/sxm/sports/channelWaterfall';
import SportsCard from '@/components/molecules/SportsCard/SportsCard';
import { Team } from '@/components/molecules/SportsCard/SportsCard';
import IconCar from '@/assets/icons/icon-car.svg';
import IconDevices from '@/assets/icons/icon-devices.svg';
import { DynamicTableRow } from '@/components/molecules/DynamicTable/DynamicTable';
import styles from './teamcard.module.scss';
import SXMLink from '@/shared-components/SXMLink';
import { useEffect, useRef, useState } from 'react';
import Script from 'next/script';
import { TeamHeroGameData } from './TeamHeroGameData';
import { Button } from '@/ui-components/atoms/Button/Button';

export interface TeamCardProps {
    gameData?: TeamHeroGameData;
    listenLiveLink?: string;
    calReplyDomain: string;
}

export default function TeamCard({ gameData, listenLiveLink, calReplyDomain }: TeamCardProps) {
    const [calReplyLoaded, setCalReplyLoaded] = useState(false);
    const calReplyBtnRef = useRef<HTMLElement>(null);

    useEffect(() => {
        if (calReplyLoaded && calReplyBtnRef.current) {
            const browserWindow = window as BrowserWindow;
            browserWindow.__CalReplyWidgetLoader.loadCustomButton(calReplyBtnRef.current);
        }
    }, [calReplyLoaded]);

    function renderChannelLink(
        deepLink: string | undefined,
        channelNumber: string | number | undefined,
        ariaLabel: string,
    ) {
        if (deepLink) {
            return (
                <SXMLink href={deepLink} target="_blank" aria-label={ariaLabel} className={styles.link}>
                    {channelNumber}
                </SXMLink>
            );
        }
        return channelNumber;
    }

    const iconStyles = {
        width: '28px',
        height: '28px',
        fill: 'var(--black)',
    };

    const moreWays: DynamicTableRow[] = [];

    let awayTeam: Team, homeTeam: Team;
    let teamCard;

    if (gameData) {
        const awayResolved = resolveChannel(gameData, 'away');
        const homeResolved = resolveChannel(gameData, 'home');

        awayTeam = {
            name: gameData.awayTeam || '',
            city: gameData.awayCity || '',
            link: {
                href: gameData.awayUrl,
            },
            logo: {
                source: gameData.awayLogo?.url || '',
                altText: '',
                width: 56,
                height: 56,
            },
            channel: awayResolved.channel?.toString() ?? '',
            isOnline: awayResolved.isOnline,
        };
        homeTeam = {
            name: gameData.homeTeam || '',
            city: gameData.homeCity || '',
            link: {
                href: gameData.homeUrl,
            },
            logo: {
                source: gameData.homeLogo?.url || '',
                altText: '',
                width: 56,
                height: 56,
            },
            channel: homeResolved.channel?.toString() ?? '',
            isOnline: homeResolved.isOnline,
        };

        moreWays.push(
            ...buildListenOptions(gameData).map((option) => ({
                rowLabel: option.label,
                items: [
                    option.channel || '-',
                    renderChannelLink(
                        option.deepLink,
                        option.internetChannel,
                        `Listen to ${gameData.awayTeamCity} @ ${gameData.homeTeamCity} on ${option.key} channel ${option.internetChannel}`,
                    ),
                ],
            })),
        );

        const moreWaysTable = (
            <DynamicTable
                columnLabels={[
                    <IconCar key={0} style={iconStyles} aria-label="In the car" />,
                    <IconDevices key={1} style={iconStyles} aria-label="On your device" />,
                ]}
                rows={moreWays}
            />
        );

        const calReplyBtn = (
            <Button
                ref={calReplyBtnRef}
                type="button"
                variant="primary"
                href={`${calReplyDomain}/`}
                ariaLabel={`get reminders for ${gameData.awayCity} ${gameData.awayTeam} at ${gameData.homeCity} ${gameData.homeTeam}`}
                data-calreply-binding
                data-event-loaded
                data-popup="lightbox"
                data-type="custombutton"
            >
                Get reminders
            </Button>
        );

        teamCard = (
            <div className={styles.cardContainer}>
                <div className={styles.card}>
                    <div className={styles.sportsCardWrapper}>
                        <SportsCard
                            startTime={gameData.gameDate || ''}
                            endTime={gameData.endDateTime || ''}
                            cta={calReplyBtn}
                            matchup={[awayTeam, homeTeam]}
                            eventDateTime={new Date(gameData.gameDate || '')}
                            theme="Hero"
                            className={styles.sportsCard}
                            moreWaysTable={moreWaysTable}
                            homeTeamColor={gameData.homeColor}
                            awayTeamColor={gameData.awayColor}
                            listenLiveLink={{ href: listenLiveLink }}
                        />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            {gameData ? teamCard : undefined}
            <Script
                id="calreply-wjs"
                src="https://assets.calreplyapp.com/scripts/widget-loader.min.js"
                onLoad={() => setCalReplyLoaded(true)}
            />
        </>
    );
}
